import { normalizeAreaName, readPublicShopLocation } from '@guildite/domain/location';
import { isValidHandle } from '@guildite/domain/handles';
import type { ShopHours } from '@guildite/domain/hours';

import type { PublicShop } from '../ports.ts';

// Builds the v2 shop view from the v2 shop record (if any) and the legacy
// RTDB public_profiles entry. Legacy coordinates are never used as a pin:
// until the seller chooses in v2, the shop shows its area only.
export function toPublicShop(shopId: string, shop: Record<string, unknown> | null, legacyProfile: Record<string, unknown> | null): PublicShop | null {
  const isLegacySeller = legacyProfile?.isSeller === true;
  if (!shop && !isLegacySeller) return null;
  // A banned seller's shop is hidden (ADMIN.md); an unban brings it back.
  if (shop?.status === 'banned') return null;
  const legacyArea = [legacyProfile?.neighbourhood, legacyProfile?.city]
    .map(part => normalizeAreaName(part))
    .filter(Boolean)
    .join(', ');
  const name = String(shop?.name || legacyProfile?.name || '').replace(/\s+/g, ' ').trim().slice(0, 100) || 'Shop';
  const photo = String(shop?.logoUrl || legacyProfile?.profilePictureUrl || '');
  return {
    id: shopId,
    handle: isValidHandle(shop?.handle) ? shop.handle : null,
    name,
    photoUrl: safeMedia(photo),
    bannerUrl: safeMedia(String(shop?.bannerUrl || legacyProfile?.bannerUrl || '')),
    bio: String(shop?.bio || legacyProfile?.bio || '').slice(0, 500),
    category: String(shop?.category || ''),
    hours: shopHours(shop, legacyProfile),
    contactOptions: contactOptions(shop),
    payoutNameVerified: shop?.payoutNameVerified === true,
    ownerId: String(shop?.ownerId || shopId),
    completedOrders: Math.max(0, Math.floor(Number(shop?.completedOrders) || 0)),
    guildIds: Array.isArray(shop?.guildIds) ? shop.guildIds.filter((id): id is string => typeof id === 'string').slice(0, 20) : [],
    location: readPublicShopLocation(shop?.location, legacyArea),
    businessMode: shop?.businessMode === 'physical' || shop?.businessMode === 'home' ? shop.businessMode : null,
  };
}

function safeMedia(url: string): string | null {
  return url.startsWith('https://') || /^http:\/\/127\.0\.0\.1:\d+\/media\//.test(url) ? url : null;
}

// v2 hours, or the legacy day-keyed hours on the old profile.
function shopHours(shop: Record<string, unknown> | null, legacy: Record<string, unknown> | null): ShopHours | null {
  const hours = shop?.hours as ShopHours | undefined;
  if (hours && typeof hours === 'object') return hours;
  const legacyWeek = legacy?.shopHours;
  return legacyWeek && typeof legacyWeek === 'object' ? { week: legacyWeek as ShopHours['week'], timeZone: 'Africa/Accra' } : null;
}

function contactOptions(shop: Record<string, unknown> | null) {
  const options = shop?.contactOptions as Record<string, unknown> | undefined;
  if (options) return { phone: options.phone === true, whatsapp: options.whatsapp === true, email: options.email === true };
  // Legacy contact availability is not consent to publish login contacts.
  return { phone: false, whatsapp: false, email: false };
}
